import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FloatingMenu, Icon, IconNames } from '@dotcom/crown-ui';
import type { FloatingMenuOption } from '@dotcom/crown-ui';
import classNames from 'classnames';
import { useToolActions } from '../../hooks/use-tool-actions';
import { useActiveCanvas } from '../../hooks/use-active-canvas';
import { useAppContext } from '../../context/app-context/app-context';
import { setAllDrawersClosed } from '../../context/app-context/app-context-actions';
import { useUIStore } from '../../stores/ui';
import { setFloatingToolMenuHost, setFloatingToolMenuInteracting } from './floating-tool-menu-host';
import styles from './floating-tool-menu.module.scss';

type FloatingToolMenuProps = {
  isSignAndSend: boolean;
  isOpen?: boolean;
  selectedZoneHasContent?: boolean;
};

export const FloatingToolMenu = ({
  isSignAndSend,
  isOpen,
  selectedZoneHasContent,
}: FloatingToolMenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const {
    appDispatch,
    appState: { isWamDrawerOpen },
  } = useAppContext();
  const closeImageEditToolbar = useUIStore((state) => state.closeImageEditToolbar);
  const closeFontToolbar = useUIStore((state) => state.closeFontToolbar);
  const closeAllDrawers = useUIStore((state) => state.closeAllDrawers);
  const canvas = useActiveCanvas();
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    setFloatingToolMenuHost(node);
  }, []);
  const handleInteractionStart = useCallback(() => {
    setFloatingToolMenuInteracting(true);
  }, []);
  const handleInteractionEnd = useCallback(() => {
    setFloatingToolMenuInteracting(false);
  }, []);
  const [snsIsOpen, setSnsIsOpen] = useState(false);
  const dismissEditingTools = useCallback(() => {
    setAllDrawersClosed(appDispatch);
    closeAllDrawers();
    closeImageEditToolbar();
    closeFontToolbar();
  }, [appDispatch, closeAllDrawers, closeFontToolbar, closeImageEditToolbar]);
  const showSnsMenu = useCallback(() => {
    dismissEditingTools();
    setSnsIsOpen(true);
  }, [dismissEditingTools]);

  useEffect(() => {
    if (!isOpen) {
      setSnsIsOpen(false);
      return;
    }
    // Don't reopen while the WAM drawer is showing
    if (isWamDrawerOpen) {
      return;
    }
    if (!selectedZoneHasContent) {
      showSnsMenu();
      return;
    }
    setSnsIsOpen(false);
  }, [isOpen, selectedZoneHasContent, isWamDrawerOpen, showSnsMenu]);

  // Auto-open menu when switching between S&S zones (selection:updated fires
  // but isOpen stays true, so the above effect doesn't re-run)
  useEffect(() => {
    if (!isSignAndSend) return;
    const c = canvas?.current;
    if (!c) return;

    const handleSelectionUpdated = () => {
      showSnsMenu();
    };
    c.on('selection:updated', handleSelectionUpdated);
    return () => {
      c.off('selection:updated', handleSelectionUpdated);
    };
  }, [isSignAndSend, canvas, showSnsMenu]);

  const { handleTextClick, handleImageClick, handleWriteAMessageClick, handleTypeAMessageClick, shouldShowPodWam } =
    useToolActions();

  const podOptions: FloatingMenuOption[] = [
    {
      label: t('floatingMenu.photo'),
      value: 'photo',
      icon: IconNames.PhotoslotBold,
    },
    {
      label: t('floatingMenu.text'),
      value: 'text',
      icon: IconNames.TextboxBold,
    },
    ...(shouldShowPodWam
      ? [
          {
            label: t('floatingMenu.handwriting'),
            value: 'handwriting',
            icon: IconNames.HallmarkSignSendBold,
          },
        ]
      : []),
  ];

  const snsOptions: FloatingMenuOption[] = [
    {
      label: t('floatingMenu.text'),
      value: 'text',
      icon: IconNames.HallmarkTypeAMessage,
    },
    {
      label: t('floatingMenu.handwriting'),
      value: 'handwriting',
      icon: IconNames.HallmarkSignSendBold,
    },
  ];

  const options = isSignAndSend ? snsOptions : podOptions;

  const handleSelect = (value: string) => {
    switch (value) {
      case 'photo':
        handleImageClick();
        break;
      case 'text':
        isSignAndSend ? handleTypeAMessageClick() : handleTextClick();
        break;
      case 'handwriting':
        handleWriteAMessageClick();
        break;
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      dismissEditingTools();
    }
    if (isSignAndSend && !selectedZoneHasContent) {
      setSnsIsOpen(open);
    }
  };

  if (isSignAndSend && selectedZoneHasContent) {
    return (
      <div
        className={styles['floating-tool-menu-container']}
        ref={hostRef}
        onPointerDownCapture={handleInteractionStart}
        onPointerUpCapture={handleInteractionEnd}
        onPointerCancelCapture={handleInteractionEnd}
        onMouseDownCapture={handleInteractionStart}
        onMouseUpCapture={handleInteractionEnd}
        onTouchStartCapture={handleInteractionStart}
        onTouchEndCapture={handleInteractionEnd}
      >
        <FloatingMenu
          options={options}
          onSelect={handleSelect}
          onOpenChange={handleOpenChange}
          transitionDirection="bottom-to-top"
          testId="floating-tool-menu"
          className={styles['floating-menu-constrained']}
          buttonAriaLabel="Edit text options"
          closeOnOutsideClick={true}
          renderTrigger={({ className, ...triggerProps }) => (
            <Button
              variant="primary"
              size="icon"
              aria-label="Edit text options"
              {...triggerProps}
              className={className}
            >
              <Icon name={IconNames.Menuclose} size="16" className={styles['floating-menu-trigger-plus']} />
            </Button>
          )}
        />
      </div>
    );
  }

  if (isSignAndSend) {
    return (
      <div
        className={classNames(
          styles['floating-tool-menu-container'],
          !isOpen && styles['floating-menu-trigger-hidden'],
        )}
        ref={hostRef}
        onPointerDownCapture={handleInteractionStart}
        onPointerUpCapture={handleInteractionEnd}
        onPointerCancelCapture={handleInteractionEnd}
        onMouseDownCapture={handleInteractionStart}
        onMouseUpCapture={handleInteractionEnd}
        onTouchStartCapture={handleInteractionStart}
        onTouchEndCapture={handleInteractionEnd}
      >
        {snsIsOpen ? (
          <FloatingMenu
            options={options}
            onSelect={handleSelect}
            onOpenChange={handleOpenChange}
            transitionDirection="bottom-to-top"
            testId="floating-tool-menu"
            className={styles['floating-menu-constrained']}
            isOpen={true}
            buttonAriaLabel="Edit text options"
            closeOnOutsideClick={true}
            renderTrigger={({ className, ...triggerProps }) => (
              <Button variant="primary" size="icon" aria-label="Close menu" {...triggerProps} className={className}>
                <Icon name={IconNames.Menuclose} size="16" />
              </Button>
            )}
          />
        ) : (
          <Button
            variant="primary"
            size="icon"
            aria-label="Open menu"
            onClick={showSnsMenu}
            data-testid="floating-tool-menu-trigger"
          >
            <Icon name={IconNames.Menuclose} size="16" className={styles['floating-menu-trigger-plus']} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={styles['floating-tool-menu-container']}>
      <FloatingMenu
        options={options}
        onSelect={handleSelect}
        onOpenChange={handleOpenChange}
        transitionDirection="bottom-to-top"
        testId="floating-tool-menu"
        className={styles['floating-menu-constrained']}
        buttonAriaLabel="Add content"
        closeOnOutsideClick={true}
      />
    </div>
  );
};
